import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { TrendingUp, TrendingDown, Target, Zap, Network, AlertCircle, ArrowRight, Activity } from 'lucide-react';

const Analytics = () => {
  const [kpis, setKpis] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [impactScores, setImpactScores] = useState([]);
  const [crossPerspective, setCrossPerspective] = useState([]);
  const [correlationMatrix, setCorrelationMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerspective, setSelectedPerspective] = useState('ALL');
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = async () => {
    const [kpisData, relsData, impactData, crossData, matrixData] = await Promise.all([
      apiFetch(`${API}/kpis`),
      apiFetch(`${API}/analytics/relationships`),
      apiFetch(`${API}/analytics/impact-scores`),
      apiFetch(`${API}/analytics/cross-perspective`),
      apiFetch(`${API}/analytics/correlation-matrix`)
    ]);

    if ([kpisData, relsData, impactData, crossData, matrixData].some(d => d?._error)) {
      console.error('Error fetching analytics:', [kpisData, relsData, impactData, crossData, matrixData].find(d => d?._error)?.message);
      setLoading(false);
      return;
    }

    setKpis(kpisData);
    setRelationships(relsData);
    setImpactScores(impactData);
    setCrossPerspective(crossData);
    setCorrelationMatrix(matrixData);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const perspectiveColors = {
    financial: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-600' },
    operational: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-600' },
    core: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-600' },
    retention_engagement: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-600' }
  };

  const filteredKPIs = selectedPerspective === 'ALL' 
    ? kpis 
    : kpis.filter(k => k.perspective === selectedPerspective);

  const getRelationshipsForKPI = (kpiId) => {
    return relationships.filter(r => 
      r.source_kpi_id === kpiId || r.target_kpi_id === kpiId
    );
  };

  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs > 0.7) return correlation > 0 ? 'bg-green-600' : 'bg-red-600';
    if (abs > 0.4) return correlation > 0 ? 'bg-green-400' : 'bg-red-400';
    if (abs > 0.2) return correlation > 0 ? 'bg-green-200' : 'bg-red-200';
    return 'bg-slate-200';
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-1">KPI Causation Analytics</h1>
        <p className="text-purple-100 text-sm">AI-powered insights across Financial, Operational, Core, and Retention perspectives</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-600 uppercase">Total KPIs</h3>
            <Target className="text-blue-600" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-900">{kpis.length}</div>
          <p className="text-xs text-slate-500 mt-1">4 perspectives</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-600 uppercase">Relationships</h3>
            <Network className="text-purple-600" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-900">{relationships.length}</div>
          <p className="text-xs text-slate-500 mt-1">connections</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-600 uppercase">Strong Correlations</h3>
            <Zap className="text-orange-600" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {relationships.filter(r => Math.abs(r.correlation) > 0.7).length}
          </div>
          <p className="text-xs text-slate-500 mt-1">high confidence</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-600 uppercase">Driver KPIs</h3>
            <TrendingUp className="text-green-600" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-900">
            {impactScores.filter(s => s.classification === 'Driver KPI').length}
          </div>
          <p className="text-xs text-slate-500 mt-1">high influence</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-slate-200">
          <div className="flex gap-1 p-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('cross-perspective')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'cross-perspective' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Cross-Perspective
            </button>
            <button
              onClick={() => setActiveTab('influence')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'influence' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              KPI Influence
            </button>
            <button
              onClick={() => setActiveTab('matrix')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'matrix' 
                  ? 'bg-purple-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Correlation Matrix
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-3">Key Analytical Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="text-blue-600" size={20} />
                      <h3 className="font-semibold text-blue-900">Top Driver KPIs</h3>
                    </div>
                    <p className="text-sm text-blue-800 mb-3">KPIs with highest influence on other metrics</p>
                    <div className="space-y-2">
                      {impactScores.filter(s => s.classification === 'Driver KPI').slice(0, 3).map((score, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-blue-900 font-medium">{score.kpi_name}</span>
                          <span className="text-blue-600 font-bold">{score.influence_score.toFixed(1)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="text-green-600" size={20} />
                      <h3 className="font-semibold text-green-900">Leading Indicators</h3>
                    </div>
                    <p className="text-sm text-green-800 mb-3">Metrics that predict future performance</p>
                    <div className="space-y-2">
                      {relationships
                        .filter(r => r.direction === 'forward' && Math.abs(r.correlation) > 0.7)
                        .slice(0, 3)
                        .map((rel, idx) => (
                          <div key={idx} className="text-sm text-green-900">
                            <span className="font-medium">{rel.source_kpi_name}</span>
                            <span className="text-green-600 mx-1">→</span>
                            <span>{rel.target_kpi_name}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Network className="text-purple-600" size={20} />
                      <h3 className="font-semibold text-purple-900">Hub KPIs</h3>
                    </div>
                    <p className="text-sm text-purple-800 mb-3">Central metrics connecting multiple areas</p>
                    <div className="space-y-2">
                      {impactScores.filter(s => s.classification === 'Hub KPI').slice(0, 3).map((score, idx) => (
                        <div key={idx} className="text-sm">
                          <div className="font-medium text-purple-900">{score.kpi_name}</div>
                          <div className="text-xs text-purple-600">
                            {score.outgoing_relationships} out / {score.incoming_relationships} in
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="text-orange-600" size={20} />
                      <h3 className="font-semibold text-orange-900">Strong Negative Correlations</h3>
                    </div>
                    <p className="text-sm text-orange-800 mb-3">Inverse relationships to monitor</p>
                    <div className="space-y-2">
                      {relationships
                        .filter(r => r.correlation < -0.7)
                        .slice(0, 3)
                        .map((rel, idx) => (
                          <div key={idx} className="text-sm">
                            <div className="text-orange-900">
                              {rel.source_kpi_name} ↔ {rel.target_kpi_name}
                            </div>
                            <div className="text-xs text-orange-600">r = {rel.correlation.toFixed(3)}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cross-Perspective Tab */}
          {activeTab === 'cross-perspective' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Cross-Perspective Relationships</h2>
                <p className="text-sm text-slate-600 mb-4">Understanding how different business areas influence each other</p>
              </div>

              <div className="space-y-3">
                {crossPerspective.map((insight, idx) => {
                  const color1 = perspectiveColors[insight.perspective_1];
                  const color2 = perspectiveColors[insight.perspective_2];
                  const strength = insight.average_correlation > 0.5 ? 'Strong' : 'Moderate';
                  
                  return (
                    <div key={idx} className="bg-white border-2 border-slate-200 rounded-lg p-5 hover:border-purple-300 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1.5 text-sm font-bold rounded-lg ${color1.bg} ${color1.text}`}>
                            {insight.perspective_1.replace('_', ' ').toUpperCase()}
                          </span>
                          <ArrowRight className="text-slate-400" size={20} />
                          <span className={`px-3 py-1.5 text-sm font-bold rounded-lg ${color2.bg} ${color2.text}`}>
                            {insight.perspective_2.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-slate-900">{insight.total_relationships}</div>
                          <div className="text-xs text-slate-500">connections</div>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-slate-700 font-medium">{insight.key_insight}</p>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Avg Correlation</div>
                          <div className="text-lg font-bold text-purple-600">{insight.average_correlation.toFixed(3)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Strong Links</div>
                          <div className="text-lg font-bold text-green-600">{insight.strong_relationships}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Strength</div>
                          <div className={`text-lg font-bold ${strength === 'Strong' ? 'text-green-600' : 'text-orange-600'}`}>
                            {strength}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* KPI Influence Tab */}
          {activeTab === 'influence' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">KPI Influence Ranking</h2>
                <p className="text-sm text-slate-600 mb-4">Ranked by influence score - how much each KPI affects others</p>
              </div>

              <div className="space-y-2">
                {impactScores.slice(0, 12).map((score, idx) => {
                  const colors = perspectiveColors[score.perspective];
                  const isDriver = score.classification === 'Driver KPI';
                  
                  return (
                    <div key={score.kpi_id} className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                      isDriver ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}>
                      <div className={`text-xl font-bold w-10 h-10 rounded-full flex items-center justify-center ${
                        idx < 3 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {idx + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900 truncate">{score.kpi_name}</span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded whitespace-nowrap ${colors.bg} ${colors.text}`}>
                            {score.perspective.replace('_', ' ')}
                          </span>
                          {isDriver && (
                            <span className="px-2 py-0.5 text-xs bg-purple-600 text-white rounded font-semibold whitespace-nowrap">
                              DRIVER
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 text-xs">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Influence:</span>
                            <span className="font-bold text-green-600">{score.influence_score.toFixed(1)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Dependency:</span>
                            <span className="font-bold text-blue-600">{score.dependency_score.toFixed(1)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Net:</span>
                            <span className={`font-bold ${score.net_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {score.net_impact.toFixed(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 ml-auto">
                            <span className="text-slate-500">{score.outgoing_relationships} out</span>
                            <span className="text-slate-400">/</span>
                            <span className="text-slate-500">{score.incoming_relationships} in</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Correlation Matrix Tab */}
          {activeTab === 'matrix' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Correlation Matrix</h2>
                <p className="text-sm text-slate-600 mb-4">Visual heatmap showing correlation strength between all KPIs</p>
              </div>

              <div className="overflow-x-auto bg-slate-50 rounded-lg p-4">
                <div className="inline-block min-w-full">
                  <div className="flex mb-2">
                    <div className="w-40"></div>
                    {correlationMatrix.slice(0, 8).map((row) => (
                      <div key={row.kpi_id} className="w-20 text-center px-1">
                        <div className="text-xs text-slate-600 font-medium transform -rotate-45 origin-left truncate h-16">
                          {row.kpi_name.substring(0, 12)}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {correlationMatrix.slice(0, 8).map((row) => (
                    <div key={row.kpi_id} className="flex items-center mb-1">
                      <div className="w-40 text-xs text-slate-700 font-medium truncate pr-2">
                        {row.kpi_name.substring(0, 25)}
                      </div>
                      {Object.entries(row.correlations).slice(0, 8).map(([kpiId, corr]) => (
                        <div key={kpiId} className="w-20 h-10 px-1">
                          <div 
                            className={`w-full h-full flex items-center justify-center text-xs font-bold text-white rounded ${getCorrelationColor(corr)}`}
                            title={`Correlation: ${corr.toFixed(3)}`}
                          >
                            {corr !== 0 ? corr.toFixed(2) : '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-slate-700 mb-3">Legend</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-600 rounded"></div>
                    <span className="text-slate-600">Strong + (&gt;0.7)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-400 rounded"></div>
                    <span className="text-slate-600">Moderate + (0.4-0.7)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-slate-200 rounded"></div>
                    <span className="text-slate-600">Neutral</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-400 rounded"></div>
                    <span className="text-slate-600">Moderate - (−0.4 to −0.7)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-600 rounded"></div>
                    <span className="text-slate-600">Strong - (&lt;−0.7)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
